import { ENVELOPE_DURATION_CAP_MINUTES, ENVELOPE_DURATION_INCREMENT_MINUTES } from "../envelope-duration";
import { InvalidCatalogDefinitionError } from "../errors.model";
import { type MemberId, memberIdSequence } from "../member-identity";
import type {
  ExamRef,
  OccurrenceJustification,
  ProtocolDefinitionProps,
  ProtocolOccurrence,
  ProtocolStep,
} from "../protocol-definition.model";
import {
  resolvePlacement,
  type StepPlacement,
  type StepPlacementInput,
  type StepPlacementProps,
} from "../step-placement";
import type { ParameterSource } from "../value.model";
import { observationLevelsProblem } from "./observation-level";
import { plannedTrialsProblem } from "./planned-trial";

const OCCURRENCE_PREFIX = "occ";
const OCCURRENCES = "occurrence";
const EXAMS = "exams";
const AUTHORED: OccurrenceJustification = { kind: "authored" };
export type PlacedProtocolStep = Omit<ProtocolStep, "placement"> & { placement: StepPlacement };
/** Shared clinical content validation for reusable and frozen one-time protocols. */
export function normalizeProtocolContent(
  props: Pick<ProtocolDefinitionProps, "steps" | "exams" | "envelopeMinutes" | "occurrenceHighWaterMark">,
) {
  assertDuration(props.envelopeMinutes);
  const exams = distinctExams(props.exams);
  const sequence = memberIdSequence(OCCURRENCE_PREFIX, props.occurrenceHighWaterMark ?? 0);
  const steps: PlacedProtocolStep[] = props.steps
    .map((step) => ({
      placement: placementFor(step),
      ...(step.title === undefined ? {} : { title: step.title }),
      occurrences: step.occurrences.map(({ plannedTrials, ...occurrence }) => ({
        ...structuredClone(occurrence),
        id: occurrence.id ?? sequence.next(),
        parameterValues: structuredClone(occurrence.parameterValues ?? []),
        ...(plannedTrials?.length ? { plannedTrials: structuredClone(plannedTrials) } : {}),
        justifications: structuredClone(occurrence.justifications ?? [AUTHORED]),
      })),
    }))
    .sort((a, b) => a.placement.offsetMinutes - b.placement.offsetMinutes);
  assertProtocolValid(steps, exams);
  const envelopeMinutes = Math.max(props.envelopeMinutes, ...steps.map((step) => step.placement.endMinutes));
  if (envelopeMinutes > ENVELOPE_DURATION_CAP_MINUTES) {
    throw new InvalidCatalogDefinitionError(
      `A protocol visit cannot exceed ${ENVELOPE_DURATION_CAP_MINUTES} minutes; this one is ${envelopeMinutes}`,
    );
  }
  return { steps, envelopeMinutes, occurrenceHigh: Math.max(props.occurrenceHighWaterMark ?? 0, sequence.high) };
}

export function placementFor(step: { placement: StepPlacementInput }): StepPlacement {
  return resolvePlacement(step.placement);
}

export function serializePlacement(placement: StepPlacement): StepPlacementProps {
  return { offsetMinutes: placement.offsetMinutes, durationMinutes: placement.durationMinutes };
}

export function serializeStep({ placement, ...step }: PlacedProtocolStep): ProtocolStep {
  return {
    ...step,
    placement: serializePlacement(placement),
  };
}

function answerKey(answer: { source: ParameterSource; parameterId: MemberId }): string {
  return `${answer.source}\0${answer.parameterId}`;
}

export function distinctExams(exams: readonly ExamRef[]): ExamRef[] {
  const seen = new Set<ExamRef>();
  for (const exam of exams) {
    if (seen.has(exam)) {
      throw new InvalidCatalogDefinitionError(`Exam '${exam}' appears twice`, { collection: EXAMS });
    }
    seen.add(exam);
  }
  // An exam twice in one visit is not meaningful, so the set is deduplicated
  // by rejection rather than by silently collapsing — a caller sending one
  // twice believes it is asking for two.
  return [...exams];
}

/**
 * Everything the protocol can decide from its own state. Run on the RESULT of
 * every structural command, which is what makes the rules impossible to route
 * around: there is no command that leaves the aggregate in a shape this would
 * refuse.
 */
function assertProtocolValid(steps: readonly PlacedProtocolStep[], exams: readonly ExamRef[]): void {
  let previousStep: PlacedProtocolStep | undefined;
  for (const step of steps) {
    if (previousStep !== undefined && !previousStep.placement.precedes(step.placement)) {
      throw new InvalidCatalogDefinitionError("Protocol steps must not overlap");
    }
    previousStep = step;
  }
  const seen = new Set<string>();
  for (const step of steps) {
    if (step.occurrences.length === 0) {
      throw new InvalidCatalogDefinitionError("Each protocol step must have at least one occurrence");
    }
    if (step.placement.durationMinutes === 0)
      throw new InvalidCatalogDefinitionError("Live work requires positive step duration");
    for (const occurrence of step.occurrences) {
      if (occurrence.qualifier !== undefined && occurrence.qualifier.trim() === "") {
        throw new InvalidCatalogDefinitionError("A qualifier must not be blank", {
          memberId: occurrence.id,
          collection: OCCURRENCES,
        });
      }
      if (occurrence.justifications.length === 0) {
        throw new InvalidCatalogDefinitionError("An occurrence must say why it is there", {
          memberId: occurrence.id,
          collection: OCCURRENCES,
        });
      }
      // Half the exam-diagnostic invariant is decidable from own state: a
      // justification naming an exam this protocol does not hold is a dangling
      // reference, and it is the half that matters for DELETION — a justified
      // occurrence is only protected while the exam is present, so a dangling
      // one would read as protected and be silently removable.
      //
      // `setWork` applies membership and provenance together. This also guards
      // HYDRATION: storage is not a trusted input.
      for (const justification of occurrence.justifications) {
        if (justification.kind === "exam" && !exams.includes(justification.exam)) {
          throw new InvalidCatalogDefinitionError(
            `Occurrence '${occurrence.id}' is justified by exam '${justification.exam}', which is not in this protocol`,
            { memberId: occurrence.id, collection: OCCURRENCES },
          );
        }
      }
      assertParameterValuesDistinct(occurrence);
      // Two occurrences of one definition must be TELLABLE APART, in the order
      // set, on the schedule and in the chart. The rule is on the pair rather
      // than on the presence of a qualifier so that adding an exam, which
      // qualifies only the occurrence it derives, cannot invalidate an
      // author's existing unqualified one.
      const key = `${occurrence.definition}\0${occurrence.qualifier ?? ""}`;
      if (seen.has(key)) {
        throw new InvalidCatalogDefinitionError(
          `'${occurrence.definition}' appears more than once and needs a qualifier to tell the occurrences apart`,
          { memberId: occurrence.id, collection: OCCURRENCES },
        );
      }
      seen.add(key);
    }
  }
}

function assertParameterValuesDistinct(occurrence: ProtocolOccurrence): void {
  if (occurrence.plannedTrials?.length && occurrence.parameterValues.length) {
    throw new InvalidCatalogDefinitionError("An occurrence with planned trials cannot answer parameters itself", {
      memberId: occurrence.id,
      collection: OCCURRENCES,
    });
  }
  const problem =
    plannedTrialsProblem(occurrence.plannedTrials ?? []) ?? observationLevelsProblem(occurrence.observationLevels);
  if (problem) throw new InvalidCatalogDefinitionError(problem, { memberId: occurrence.id, collection: OCCURRENCES });
  const seen = new Set<string>();
  for (const answer of occurrence.parameterValues) {
    const key = answerKey(answer);
    if (seen.has(key)) {
      throw new InvalidCatalogDefinitionError(`Parameter '${answer.parameterId}' is answered twice`, {
        memberId: occurrence.id,
        collection: OCCURRENCES,
      });
    }
    seen.add(key);
  }
}

export function assertDuration(minutes: number): void {
  const valid =
    Number.isFinite(minutes) &&
    minutes > 0 &&
    minutes % ENVELOPE_DURATION_INCREMENT_MINUTES === 0 &&
    minutes <= ENVELOPE_DURATION_CAP_MINUTES;
  if (!valid) {
    throw new InvalidCatalogDefinitionError(
      `A duration must be a positive ${ENVELOPE_DURATION_INCREMENT_MINUTES}-minute increment no greater than ${ENVELOPE_DURATION_CAP_MINUTES} minutes, got ${minutes}`,
    );
  }
}
